import {
    delete_document,
    delete_folder,
    get_all_documents_by_folder,
    upload_document
} from "../Services/referentiel.service";
import { ReferentielActionTypes } from "./actionTypes";
import { message } from "antd";
import i18n from "../i18n";

const UNCATEGORIZED_KEY = "__uncategorized__"; // ✅ Constante technique globale

// 🔥 Charger tous les documents et en extraire les dossiers
export function RequestGetDocumentsAndFolders() {
    return async function (dispatch) {
        dispatch(setDocumentLoading(true));
        try {
            const res = await get_all_documents_by_folder();
            if (res.success && res.data) {
                const docs = res.data;
                dispatch(setDocuments(docs));

                // ✅ Si aucun document, ne créer AUCUN folder
                if (docs.length === 0) {
                    dispatch(setFolders([]));
                    return;
                }

                // ✅ Extraire les folders uniques, en utilisant la clé technique
                const uniqueFolders = [...new Set(
                    docs.map((doc) => {
                        const folder = doc.folder?.trim();
                        // Si vide ou null, utiliser la clé technique
                        return folder || UNCATEGORIZED_KEY;
                    })
                )];

                const folderList = uniqueFolders.map((folder) => ({
                    key: folder,
                    title: folder, // Le titre sera traduit dans l'UI
                }));

                dispatch(setFolders(folderList));
            } else {
                dispatch(setReferentielError(res.error || res.message || i18n.t("referentiel:messages.loadError")));
            }
        } catch (err) {
            dispatch(setReferentielError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.response?.data?.detail ||
                i18n.t("referentiel:messages.server")
            ));
        } finally {
            dispatch(setDocumentLoading(false));
        }
    };
}

export function RequestGetDocumentsRef() {
    return async function (dispatch) {
        dispatch(setDocumentLoading(true));
        try {
            const res = await get_all_documents_by_folder();
            if (res.success && res.data) {
                const docs = res.data;
                dispatch(setDocuments(docs));
            } else {
                dispatch(setReferentielError(res.error || res.message || i18n.t("referentiel:messages.loadError")));
            }
        } catch (err) {
            dispatch(setReferentielError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.response?.data?.detail ||
                i18n.t("referentiel:messages.server")
            ));
        } finally {
            dispatch(setDocumentLoading(false));
        }
    };
}

// 📤 Importer un document et l'ajouter à la liste Redux
export function RequestUploadDocument(formData, onUploadSuccess, setShowModal) {
    return async function (dispatch) {
        try {
            dispatch(setLoadingUploadReferentiel(true));
            const res = await upload_document(formData);
            if (res.success && res.data) {
                // ✅ Normaliser le folder : vide/null → UNCATEGORIZED_KEY
                const folderValue = res.data.folder?.trim();
                const doc = {
                    ...res.data,
                    folder: folderValue || UNCATEGORIZED_KEY,
                };

                dispatch(addDocument(doc));
                if (onUploadSuccess) {
                    onUploadSuccess(doc.folder);
                    setShowModal(false);
                }
            } else {
                dispatch(setReferentielError(res.error || res.message || i18n.t("referentiel:messages.unknown")));
            }
        } catch (err) {
            dispatch(setReferentielError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.response?.data?.detail ||
                i18n.t("referentiel:messages.server")
            ));
        } finally {
            dispatch(setLoadingUploadReferentiel(false));
        }
    };
}

export function RequestDeleteDocument(fileId) {
    return async function (dispatch, getState) {
        try {
            const res = await delete_document(fileId);
            if (res.success) {
                const currentDocs = getState().files?.documents || [];
                const newDocs = currentDocs.filter(doc => doc.id !== fileId);
                dispatch(setDocuments(newDocs));

                // ✅ Recalculer les folders après suppression
                if (newDocs.length === 0) {
                    dispatch(setFolders([]));
                } else {
                    const uniqueFolders = [...new Set(
                        newDocs.map((doc) => {
                            const folder = doc.folder?.trim();
                            return folder || UNCATEGORIZED_KEY;
                        })
                    )];
                    const folderList = uniqueFolders.map((folder) => ({
                        key: folder,
                        title: folder,
                    }));
                    dispatch(setFolders(folderList));
                }

                message.success(i18n.t("referentiel:messages.deleted"));
            } else {
                dispatch(setReferentielError(res.error || res.message || i18n.t("referentiel:messages.unknown")));
            }
        } catch (err) {
            dispatch(setReferentielError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.response?.data?.detail ||
                i18n.t("referentiel:messages.server")
            ));
        }
    };
}

export function RequestDeleteFolder(folderName) {
    return async function (dispatch, getState) {
        try {
            const res = await delete_folder(folderName);
            if (res.success) {
                const allDocs = getState().files.documents;
                const updatedDocs = allDocs.filter(doc => {
                    const docFolder = doc.folder?.trim() || UNCATEGORIZED_KEY;
                    return docFolder !== folderName;
                });

                dispatch(setDocuments(updatedDocs));
                message.success(i18n.t("referentiel:messages.folderDeleted"));

                // ✅ Recalculer les folders après suppression
                if (updatedDocs.length === 0) {
                    dispatch(setFolders([]));
                } else {
                    const uniqueFolders = [...new Set(
                        updatedDocs.map((doc) => {
                            const folder = doc.folder?.trim();
                            return folder || UNCATEGORIZED_KEY; // ✅ Utiliser la clé technique
                        })
                    )];
                    const folderList = uniqueFolders.map((folder) => ({
                        key: folder,
                        title: folder,
                    }));
                    dispatch(setFolders(folderList));
                }
            } else {
                dispatch(setReferentielError(res.error || res.message || i18n.t("referentiel:messages.unknown")));
            }
        } catch (err) {
            dispatch(setReferentielError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.response?.data?.detail ||
                i18n.t("referentiel:messages.server")
            ));
        }
    };
}

// 🧱 Action creators
export const setDocuments = (docs) => ({
    type: ReferentielActionTypes.SET_DOCUMENTS,
    payload: docs,
});

export const setDocumentLoading = (isLoading) => ({
    type: ReferentielActionTypes.SET_DOCUMENT_LOADING,
    payload: isLoading,
});

export const setFolders = (folders) => ({
    type: ReferentielActionTypes.SET_FOLDERS,
    payload: folders,
});

export const addDocument = (doc) => ({
    type: ReferentielActionTypes.ADD_DOCUMENT,
    payload: doc,
});

export const setLoadingUploadReferentiel = (isLoading) => ({
    type: ReferentielActionTypes.SET_LOADING_REFERENTIEL,
    payload: isLoading,
});

export const setReferentielError = (errorMessage) => ({
    type: ReferentielActionTypes.SET_ERROR,
    payload: errorMessage,
});
